import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { CheckCircle, CopySimple, EnvelopeSimple, LinkSimple, Storefront, UserCircle } from "@phosphor-icons/react";
import moment from "moment";
import toast from "react-hot-toast";
import ResponsivePagination from "react-responsive-pagination";
import "react-responsive-pagination/themes/classic-light-dark.css";
import PageFrame from "../../common/PageFrame";
import Loader from "../../common/Loader";
import { eventActions } from "../../redux/actions";
import { toRosterRows } from "../../helpers/rosterRows";

const ManageVendors = () => {
    const { eventId } = useParams();
    const dispatch = useDispatch();
    const [page, setPage] = useState(1);
    const [pageCount, setPageCount] = useState(1);
    const [vendorCount, setVendorCount] = useState(0);
    const [vendors, setVendors] = useState([]);
    const inviteLink = `https://no-q.ca/register?eventId=${eventId}`;

    const {
        isFetchingSingleEvent,
        isFetchedSingleEventSuccess,
        isFetchingEventVendors,
        isFetchedEventVendorsSuccess,
    } = useSelector((store) => store.eventManager);

    useEffect(() => {
        if (eventId) dispatch(eventActions.getSingleEvent(eventId));
    }, [dispatch, eventId]);

    useEffect(() => {
        if (eventId) dispatch(eventActions.getEventVendors(eventId, page));
    }, [dispatch, eventId, page]);

    useEffect(() => {
        if (!isFetchedEventVendorsSuccess) return;
        setVendors(isFetchedEventVendorsSuccess?.data || []);
        setPageCount(isFetchedEventVendorsSuccess?.meta?.last_page || 1);
        setVendorCount(isFetchedEventVendorsSuccess?.meta?.total || isFetchedEventVendorsSuccess?.data?.length || 0);
    }, [isFetchedEventVendorsSuccess]);

    const handleCopyInviteLink = async () => {
        await navigator.clipboard.writeText(inviteLink);
        toast.success("Invite link copied");
    };

    const event = isFetchedSingleEventSuccess || {};
    const rows = toRosterRows(vendors, eventId);

    return (
        <PageFrame
            isLoading={isFetchingSingleEvent}
            frameLinks={[
                { text: "Event Details", url: `/dashboard/event/${eventId}`, isActive: false },
                { text: "Vendors & invitations", url: `/dashboard/event/${eventId}/invitations`, isActive: true },
                { text: "Event insights", url: `/dashboard/event/${eventId}/insights`, isActive: false },
            ]}
            childrenContainerClass="manage-vendors-main"
        >
            <div className="manage-vendors-page">
                <header className="manage-vendors-header">
                    <div>
                        <p className="noq-page-eyebrow">EVENT PARTICIPANTS</p>
                        <h1>Vendors & invitations</h1>
                        <p>
                            {event?.name || "Event"}
                            {event?.start_date && event?.end_date
                                ? ` · ${moment(event.start_date).format("MMM D, YYYY")}–${moment(event.end_date).format("MMM D, YYYY")}`
                                : ""}
                        </p>
                    </div>
                    <span className="vendor-count-badge">{vendorCount} vendor{vendorCount === 1 ? "" : "s"}</span>
                </header>

                <section className="vendor-invite-panel">
                    <div className="vendor-section-heading">
                        <div className="vendor-section-icon"><EnvelopeSimple size={21} /></div>
                        <div><h2>Invite vendors</h2><p>Share this link with vendors you want to join the event.</p></div>
                    </div>
                    <div className="vendor-invite-row">
                        <div className="vendor-invite-link"><LinkSimple size={19} /><span>{inviteLink}</span></div>
                        <button type="button" onClick={handleCopyInviteLink}><CopySimple size={18} weight="bold" />Copy invite link</button>
                    </div>
                </section>

                <section className="vendor-roster-panel">
                    <div className="vendor-roster-heading">
                        <div><h2>Vendor roster</h2><p>Confirmed businesses participating in this event.</p></div>
                        <span>{vendorCount} total</span>
                    </div>

                    {isFetchingEventVendors ? (
                        <div className="vendor-roster-state"><Loader color="#CEF24E" /><p>Loading vendors</p></div>
                    ) : vendors.length ? (
                        <div className="vendor-table-wrap">
                            <table className="vendor-table">
                                <thead><tr><th>Store</th><th>Vendor</th><th>Contact</th><th>Phone</th><th>Status</th></tr></thead>
                                <tbody>
                                    {rows.map(({ key, vendor, store }) => {
                                        const isLive = store?.status?.toLowerCase() === "published";
                                        return (
                                            <tr key={key}>
                                                <td>
                                                    <div className="vendor-name-cell">
                                                        <span>{store?.banner_url ? <img src={store.banner_url} alt="" /> : <Storefront size={18} />}</span>
                                                        <strong>{store?.name || "No store linked"}</strong>
                                                    </div>
                                                </td>
                                                <td><div className="vendor-owner-cell"><UserCircle size={16} />{vendor?.name || "Unnamed vendor"}</div></td>
                                                <td>{vendor?.email || "—"}</td>
                                                <td>{vendor?.phone_number || "—"}</td>
                                                <td>
                                                    {!store ? (
                                                        <span className="vendor-confirmed"><CheckCircle size={14} weight="fill" />Confirmed</span>
                                                    ) : (
                                                        <span className={isLive ? "vendor-confirmed" : "vendor-draft"}>{isLive ? <CheckCircle size={14} weight="fill" /> : null}{isLive ? "Live" : "Draft"}</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="vendor-roster-state"><Storefront size={34} /><h3>No vendors yet</h3><p>Copy the invitation link above to start building the event roster.</p></div>
                    )}

                    {pageCount > 1 && <div className="vendor-pagination"><ResponsivePagination current={page} total={pageCount} maxWidth={260} onPageChange={setPage} /></div>}
                </section>
            </div>
        </PageFrame>
    );
};

export default ManageVendors;
